import React from 'react';
import { ResourceModule } from '../../features/common/ResourceModule';
import { registerForms } from '../../features/common/formRegistry';
import { TabbedPage } from '../../features/common/TabbedPage';
import { animalTypesApi, assetTypesApi, assetsApi, breedsApi } from '../../services/resources';
import { formatCurrency, formatDate } from '../../utils/format';
import { ILMSStatusBadge } from '../../components/ILMSStatusBadge';
import { cn } from '../../utils/cn';
import type { Asset, AnimalType, AssetType, Breed } from '../../types';
import type { ModuleConfig } from '../../features/common/types';

// Page-vs-tab call: Assets is the generic superclass behind Animals/Poultry
// Batches/Coconut Trees/Equipment (REQ-003) — its catalogue tables (Asset
// Types) and the two animal-specific catalogues (Animal Types, Breeds) live
// here as tabs since they're all small reference lists, not full workflows.

const activeBadge = (active: boolean) => (
  <span className={cn('inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border',
    active ? 'bg-success-50 text-success-700 border-success-100' : 'bg-surface-muted text-ink-500 border-ink-300/30')}>
    {active ? 'Active' : 'Inactive'}
  </span>
);

const assetsConfig: ModuleConfig<Asset> = {
  title: 'Assets', entityLabel: 'asset', module: 'Animals', queryKey: 'assets',
  // Assets is read-only here (created indirectly via Animals/Poultry/Coconut
  // forms), so assetsApi's stricter AssetCreate-typed create() — never
  // actually invoked — just needs a type-compatible cast.
  api: assetsApi as unknown as ModuleConfig<Asset>['api'],
  idKey: 'id',
  referenceKeys: ['farms', 'assetTypes', 'locations'], readOnly: true,
  searchPlaceholder: 'Search by asset code…',
  columns: [
    { key: 'code', header: 'Asset Code', primary: true, render: (r) => r.asset_code },
    { key: 'name', header: 'Name', render: (r) => r.asset_name || '—' },
    { key: 'status', header: 'Status', render: (r) => <ILMSStatusBadge status={r.status} size="sm" /> },
    { key: 'purchase', header: 'Purchase (₹)', align: 'right', hideOnTablet: true, render: (r) => (r.purchase_cost != null ? formatCurrency(r.purchase_cost) : '—') },
    { key: 'transport', header: 'Transport (₹)', align: 'right', hideOnTablet: true, render: (r) => (r.transport_cost != null ? formatCurrency(r.transport_cost) : '—') },
    { key: 'insurance', header: 'Insurance (₹)', align: 'right', hideOnTablet: true, render: (r) => (r.insurance_cost != null ? formatCurrency(r.insurance_cost) : '—') },
    // Purchase + transport + insurance, unless a value was set on the asset.
    { key: 'value', header: 'Current Value (₹)', align: 'right', render: (r) => (r.current_value != null ? formatCurrency(r.current_value) : '—') },
    { key: 'purchased', header: 'Purchase Date', render: (r) => formatDate(r.purchase_date), hideOnTablet: true },
  ],
  fields: [],
};

const assetTypesConfig: ModuleConfig<AssetType> = {
  title: 'Asset Types', entityLabel: 'asset type', module: 'System Settings', queryKey: 'asset-types', api: assetTypesApi, idKey: 'id',
  defaultValues: { active: true },
  columns: [
    { key: 'name', header: 'Name', primary: true, render: (r) => r.name },
    { key: 'category', header: 'Category', render: (r) => r.category || '—' },
    { key: 'active', header: 'Status', render: (r) => activeBadge(r.active) },
  ],
  fields: [
    { name: 'name', label: 'Name', type: 'text', section: 'Details', required: true, placeholder: 'Animal, PoultryBatch, CoconutTree, Equipment…' },
    { name: 'category', label: 'Category', type: 'text', section: 'Details', optional: true },
    { name: 'description', label: 'Description', type: 'text', section: 'Details', optional: true },
    { name: 'active', label: 'Active', type: 'checkbox', section: 'Details' },
  ],
};

const animalTypesConfig: ModuleConfig<AnimalType> = {
  title: 'Animal Types', entityLabel: 'animal type', module: 'System Settings', queryKey: 'animal-types', api: animalTypesApi, idKey: 'id',
  defaultValues: { active: true },
  columns: [
    { key: 'name', header: 'Name', primary: true, render: (r) => r.name },
    { key: 'description', header: 'Description', render: (r) => r.description || '—' },
    { key: 'breeding', header: 'Gestation / Heat / PD / Dry-off (days)', hideOnTablet: true, render: (r) => (
      [r.gestation_days, r.heat_cycle_days, r.pd_check_days, r.dry_off_days].some((v) => v != null)
        ? [r.gestation_days, r.heat_cycle_days, r.pd_check_days, r.dry_off_days].map((v) => v ?? 'default').join(' / ')
        : 'Defaults'
    ) },
    { key: 'active', header: 'Status', render: (r) => activeBadge(r.active) },
  ],
  fields: [
    { name: 'name', label: 'Name', type: 'text', section: 'Details', required: true },
    { name: 'description', label: 'Description', type: 'text', section: 'Details', optional: true },
    { name: 'active', label: 'Active', type: 'checkbox', section: 'Details' },
    // Drive the Breeding tab's repeat-heat, PD-due, expected-calving and
    // dry-off dates. Blank = the usual figures.
    { name: 'gestation_days', label: 'Gestation Days', type: 'number', section: 'Breeding Days', optional: true, min: 1,
      helperText: 'Blank = 280 (cow) or 310 (buffalo, if the name contains "buffalo").' },
    { name: 'heat_cycle_days', label: 'Heat Cycle Days', type: 'number', section: 'Breeding Days', optional: true, min: 1,
      helperText: 'Repeat heat check = AI date + this. Blank = 21.' },
    { name: 'pd_check_days', label: 'PD Check After AI (days)', type: 'number', section: 'Breeding Days', optional: true, min: 1,
      helperText: 'PD due = AI date + this. Blank = 60.' },
    { name: 'dry_off_days', label: 'Dry-off Before Calving (days)', type: 'number', section: 'Breeding Days', optional: true, min: 0,
      helperText: 'Dry-off date = expected calving − this. Blank = 60.' },
  ],
};

const breedsConfig: ModuleConfig<Breed> = {
  title: 'Breeds', entityLabel: 'breed', module: 'System Settings', queryKey: 'breeds', api: breedsApi, idKey: 'id',
  referenceKeys: ['animalTypes'], defaultValues: { active: true },
  columns: [
    { key: 'name', header: 'Name', primary: true, render: (r) => r.name },
    { key: 'active', header: 'Status', render: (r) => activeBadge(r.active) },
  ],
  fields: [
    { name: 'name', label: 'Name', type: 'text', section: 'Details', required: true },
    { name: 'animal_type_id', label: 'Animal Type', type: 'select', section: 'Details', optional: true, optionsKey: 'animalTypes' },
    { name: 'description', label: 'Description', type: 'text', section: 'Details', optional: true },
    { name: 'active', label: 'Active', type: 'checkbox', section: 'Details' },
  ],
};

export default function AssetsRegistryPage() {
  return (
    <TabbedPage
      title="Assets Registry"
      subtitle="Every farm asset — animals, poultry batches, coconut trees and equipment — extends this generic registry."
      tabs={[
        { id: 'assets', label: 'Assets', render: () => <ResourceModule config={assetsConfig} /> },
        { id: 'asset-types', label: 'Asset Types', render: () => <ResourceModule config={assetTypesConfig} /> },
        { id: 'animal-types', label: 'Animal Types', render: () => <ResourceModule config={animalTypesConfig} /> },
        { id: 'breeds', label: 'Breeds', render: () => <ResourceModule config={breedsConfig} /> },
      ]}
    />
  );
}

// For Administration -> Entry Form Settings.
registerForms('Assets Registry', assetsConfig, assetTypesConfig, animalTypesConfig, breedsConfig);
